import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiLogin } from '../services/auth';
import useSeo from '../hooks/useSeo';

export default function Login() {
  useSeo({ title: 'Log in', description: 'Log in to your Maa Florist Mumbai account.', canonical: '/login' });
  const [form, setForm] = useState({ login: '', password: '' });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const next = loc.state?.next || '/account';

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); 
    setBusy(true);
    
    try {
      const res = await apiLogin(form);
      const { user, token } = res;
      login(user, token);
      nav(next);
    } catch (error) {
      // 👇 1. Add these console logs so you can see the exact shape in your browser console
      console.log('❌ Full Error Object:', error);
      
      // 👇 2. Specifically grab error.data first, because http.js puts the JSON there
      const data = error.data || error.response?.data || error;
      console.log('✅ Extracted Backend Data:', data);

      // 👇 3. Now the redirect logic will finally trigger
      if (data?.verification_required === true && data?.email) {
        nav('/register', { 
          state: { 
            verify_email: data.email, 
            dev_otp: data.dev_otp 
          } 
        });
        return;
      }
      
      setErr(data?.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] grid place-items-center bg-[#f6f2ea] px-4 py-12">
      <form onSubmit={submit} className="bg-white w-full max-w-md rounded-xl shadow-sm border border-neutral-200 p-8">
        <div className="text-center mb-6">
          <h1 className="font-serif text-3xl text-neutral-900">Welcome back</h1>
          <p className="text-sm text-neutral-600 mt-1">Log in with your email or mobile number.</p>
        </div>

        {err && <div className="mb-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2">{err}</div>}

        <label className="text-xs font-medium text-neutral-600 flex items-center gap-1"><Mail className="w-3 h-3" /> Email or mobile</label>
        <input
          required
          value={form.login}
          onChange={(e) => setForm({ ...form, login: e.target.value })}
          placeholder="you@example.com or 9876543210"
          className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
        />

        <label className="mt-4 text-xs font-medium text-neutral-600 flex items-center gap-1"><Lock className="w-3 h-3" /> Password</label>
        <input
          type="password"
          required
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
        />

        <button disabled={busy} className="mt-6 w-full bg-[#1e3a2b] hover:bg-[#2a5040] disabled:opacity-60 text-white font-bold py-3 rounded-md tracking-wide transition-colors flex items-center justify-center gap-2">
          <LogIn className="w-4 h-4" /> {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="mt-4 flex items-center justify-between text-sm">
          <Link to="/forgot" className="text-[#1e3a2b] hover:underline">Forgot password?</Link>
          <Link to="/register" className="text-[#1e3a2b] hover:underline">Create account</Link>
        </div>
      </form>
    </main>
  );
}